import React, { useState, useRef, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Popper, Grow, Paper, Typography, Stack, Box, Divider, Grid } from "@mui/material";
import { IconButton, Tooltip } from "@mzc-pdc/ui";
import { useTranslation } from "react-i18next";
import { ImageBucketS3On, IconClose, IconArrowUpload, IconArrowDownload, IconLock } from "@mzc-cloudplex/icons";
import theme from "@styles/theme";
import { patchBulkSearchableBucketsLock } from "@modules/searchableBuckets";
import convertUtcToLocal from "@cores/convertUtcToLocal";
import { setNotification } from "@modules/notification";

const BucketLockSettingPopper = ({ open, anchorRef, onHandleClickClose, onChangeBucketLock, getBucketLockIcon }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const { bucketItems, stage, project, searchableBuckets } = useSelector(
        ({ sources, stage, project, searchableBuckets }) => ({
            bucketItems: sources.hierarchy?.folder?.data,
            stage: stage,
            project: project,
            searchableBuckets: searchableBuckets,
        }),
    );

    const onChangeLock = async (item) => {
        // console.log(item.isLocked);
        try {
            const data = { searchableBucketId: item.id };
            const status = item.isLocked ? "unlock" : "lock";

            const response = await dispatch(patchBulkSearchableBucketsLock(status, data));

            onChangeBucketLock(response);

            if (response.isLocked) {
                dispatch(
                    setNotification({
                        type: "success",
                        contents: t(`common::msg::${"Bucket '{{name}}' is feature locked for other users."}`, {
                            name: item.name,
                        }),
                    }),
                );
            } else {
                dispatch(
                    setNotification({
                        type: "success",
                        contents: t(`common::msg::${"Bucket '{{name}}' is feature unlocked for another user."}`, {
                            name: item.name,
                        }),
                    }),
                );
            }
        } catch (e) {
            if (!item.isLocked) {
                dispatch(
                    setNotification({
                        type: "error",
                        contents: t(
                            `common::msg::${"Error occurred while feature-locking bucket '{{name}}' for another user. {{line}} Please refresh the page and try again."}`,
                            {
                                name: item.name,
                                line: "\n",
                            },
                        ),
                    }),
                );
            } else {
                dispatch(
                    setNotification({
                        type: "error",
                        contents: t(
                            `common::msg::${"Error occurred while attempting to feature unlock bucket '{{name}}' for another user. {{line}} Please refresh the page and try again."}`,
                            {
                                name: item.name,
                                line: "\n",
                            },
                        ),
                    }),
                );
            }
        }
    };

    const getBucketLockDisabled = useCallback(
        (item) => {
            if (item.isLocked) {
                const isProjectAdmin = stage?.user?.projects.find((p) => p.id === project.id)?.isAdmin;
                const status = stage?.user?.isSpaceAdmin || isProjectAdmin || item.lockedBy.id === stage?.user?.id;
                return !status;
            }

            return false;
        },
        [bucketItems, stage, project],
    );

    return (
        <Popper
            open={open}
            anchorEl={anchorRef}
            placement={`bottom-start`}
            paperOptions={`bottom-start`}
            transition
            disablePortal
            style={{ zIndex: 1000, position: "absolute" }}
        >
            {({ TransitionProps }) => (
                <Grow {...TransitionProps}>
                    <Paper
                        sx={{
                            mt: "21px",
                            p: `20px`,
                            boxShadow: `0 4px 8px 0 rgba(0, 0, 0, 0.16)`,
                            zIndex: 1000,
                        }}
                        variant={"outlined"}
                    >
                        <Box>
                            <Stack spacing={"9px"} direction={"row"}>
                                <Typography
                                    flex={1}
                                    variant={"subtitle1_700"}
                                    component={`p`}
                                    sx={{ display: "flex", alignItems: "center" }}
                                >
                                    {t(`common::label::Set lockout for other users`)}
                                </Typography>
                                <Tooltip title={t(`common::label::Close`, "Close")}>
                                    <IconButton onClick={onHandleClickClose} circled size={"medium"}>
                                        <IconClose size={16} />
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                            <Typography variant="body4_400" color={"#686868"}>
                                {t(
                                    `common::msg::${"This setting can lock the bucket so that other users cannot use the storage’s features (Copy, Move, Rename, Delete)."}`,
                                )}
                            </Typography>

                            <Divider flexItem sx={{ mt: "20px", mb: "16px" }} />
                            {bucketItems?.map((r) => {
                                return (
                                    <Stack mt={3} gap={"6px"} key={r.name}>
                                        <Stack direction={"row"} gap={"6px"} alignItems={"center"}>
                                            {r.type === "bucket" && (
                                                <Grid container width={"auto"} alignItems={"center"} gap={0.25}>
                                                    <span>
                                                        <ImageBucketS3On width={16} height={16} />
                                                    </span>
                                                    {r.type === "bucket" && (
                                                        <Grid item>
                                                            <Grid
                                                                container
                                                                flexDirection={"column"}
                                                                gap={0.5}
                                                                sx={{ lineHeight: 1 }}
                                                            >
                                                                {r.uploadable ? (
                                                                    <IconArrowUpload size={6} htmlColor={"#2763ea"} />
                                                                ) : (
                                                                    <IconArrowUpload size={6} color={"disabled"} />
                                                                )}
                                                                {r.downloadable ? (
                                                                    <IconArrowDownload
                                                                        size={6}
                                                                        htmlColor={"#2763ea"}
                                                                        style={{ margin: 0 }}
                                                                    />
                                                                ) : (
                                                                    <IconArrowDownload
                                                                        size={6}
                                                                        color={"disabled"}
                                                                        style={{ margin: 0 }}
                                                                    />
                                                                )}
                                                            </Grid>
                                                        </Grid>
                                                    )}
                                                </Grid>
                                            )}
                                            <Typography
                                                variant="body2_400"
                                                title={r.name}
                                                flex={1}
                                                color={theme.palette.grey.dark}
                                            >
                                                {r.name}
                                            </Typography>
                                            <div className={`form-switch-label`}>
                                                {getBucketLockIcon(r)}

                                                <Typography variant="body2_400" sx={{ ml: "6px", mr: "12px" }}>
                                                    {t(`common::label::${r.isLocked ? "Lock" : "Unlock"}`)}
                                                </Typography>
                                                <Stack sx={{ width: "28px" }} alignItems={"center"}>
                                                    {searchableBuckets.bucketsLockPendingList.find(
                                                        (v) => v === r.id,
                                                    ) !== undefined ? (
                                                        <i className="sprite sprite-loading"></i>
                                                    ) : (
                                                        <label className="form-switch form-switch-sm">
                                                            <input
                                                                type="checkbox"
                                                                name="isBucketRestrictedToCdnToggle"
                                                                checked={r.isLocked}
                                                                disabled={getBucketLockDisabled(r)}
                                                                onChange={(e) => {
                                                                    onChangeLock(r);
                                                                }}
                                                            />
                                                            <i></i>
                                                        </label>
                                                    )}
                                                </Stack>
                                            </div>
                                        </Stack>
                                        {r.isLocked && (
                                            <Stack flex={1} alignItems={"end"}>
                                                <Typography variant="body4_400" align={"right"} color="#686868">
                                                    {r.lockedBy.id !== stage?.user?.id
                                                        ? `${t(`common::label::locked by '{{name}}({{username}})'`, {
                                                              name: r.lockedBy?.name,
                                                              username: r.lockedBy?.username,
                                                          })}`
                                                        : `${t(`common::label::locked by 'Me'`)}`}
                                                </Typography>
                                                <Typography variant="body4_400" align={"right"} color="#686868">
                                                    {convertUtcToLocal(r.lockedAt)}
                                                </Typography>
                                            </Stack>
                                        )}
                                    </Stack>
                                );
                            })}
                            {/* <Divider flexItem sx={{ mt: "28px", mb: "12px" }} />
                            <Box sx={{ textAlign: `right` }}>
                                <Button
                                    sx={{ mr: "5px" }}
                                    type={"button"}
                                    variant={"text"}
                                    size={"small"}
                                    color={"inherit"}
                                    minWidth={1}
                                    onClick={onHandleClickClose}
                                >
                                    {t("common::label::Cancel")}
                                </Button>
                                <Button
                                    type={"button"}
                                    variant={"contained"}
                                    size={"small"}
                                    color={"primary"}
                                    onClick={() => {}}
                                >
                                    {t("common::label::Apply")}
                                </Button>
                            </Box> */}
                        </Box>
                    </Paper>
                </Grow>
            )}
        </Popper>
    );
};

export default BucketLockSettingPopper;
